import React, { useEffect, useState } from 'react';
import { isMobile } from 'lib/utils/UiUtils';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import MerGraphController from './MerGraphController';
import { formatCurrency } from 'lib/utils/currency';
import { orZero } from 'lib/utils/NumUtils';
import './style.scss';
// import ChartDataLabels from 'chartjs-plugin-datalabels';

const gradientColors = {
    'y1': ['rgba(102, 255, 204, 0.4)', 'rgba(102, 255, 204, 0)'],
    'y2': ['rgba(243, 63, 255, 0.4)', 'rgba(243, 63, 255, 0)'],
    'y3': ['rgba(246, 247, 248, 0.4)', 'rgba(246, 247, 248, 0)'],
}

const widthBreakpoints = [
    {
        width: 400,
        fontSize: 10
    },
    {
        width: 800,
        fontSize: 12
    },
    {
        width: 1100,
        fontSize: 14
    },
]

export default MerGraph = ({
    data,
    options,
    dailyGoal,
    ...props
}) => {
    const chartRef = React.useRef();
    const mobile = isMobile();
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    let chart = null;
    let resizeTimeoutId;

    useEffect(() => {

        // test
        //data.datasets[0].data = data.datasets[0].data.map(d => Math.round(100*Math.random()));

        Chart.register(...registerables);
        Chart.register(annotationPlugin);

        // https://www.chartjs.org/docs/latest/axes/styling.html#grid-line-configuration

        Chart.defaults.elements.bar.borderRadius = mobile ? 5 : 10;
        Chart.defaults.elements.bar.pointRadius = mobile ?   5 : 10;
        Chart.defaults.elements.bar.barThickness = mobile ? 5 : 10;
        Chart.defaults.scale.ticks.padding = mobile ? 10 : 20;

        // Chart.defaults.borderColor = 'rgba(255,255,255,.4)';
        Chart.defaults.color = 'rgba(255,255,255,1)';
        Chart.defaults.font.family = 'Grotesk Bold';
        setFontSize();

        try {
            let ctx = chartRef.current.getContext('2d');
            chart = new Chart(ctx, { type: 'line', data: data, /*plugins: [ChartDataLabels],*/ options: options });
        } catch(err) {
            console.log(err);
        }

        return () => {
            if (chart && chart.canvas) {
                chart.destroy();
            }
        }

    }, [data]);

    useEffect(() => {
        if (chart && chart.canvas) {
            updateChart();
        }
    }, [chart, data]);

    useEffect(() => {
        let isMounted = true;
        const _handleResize = () => {
            if (isMounted) {
                handleResize();
            }
        }

        if (isMounted) {
            handleResize();
            window.addEventListener('resize', _handleResize);
        }
        return () => {
            isMounted = false;
            window.removeEventListener('resize', _handleResize);
            window.clearTimeout(resizeTimeoutId);
        }
    }, [])

    useEffect(() => {
        setFontSize();
    }, [screenWidth])

    const updateData = (d) => {
        try {
            if (d) {
                const scaleId = d.yAxisID;

                const maxScale = chart.scales[scaleId].max;
                const minScale = chart.scales[scaleId].min;

                const maxData = d.data.length ? Math.max(...d.data) : 0;
                const maxHeight = chart.scales[scaleId].maxHeight;

                const yStart = maxHeight - (maxHeight * ((maxData - minScale) / (maxScale - minScale))) || 0;

                if (chart.ctx) {
                    const gradient = chart.ctx.createLinearGradient(0, yStart, 0, maxHeight);
                    gradient.addColorStop(0, gradientColors[scaleId][0]);
                    gradient.addColorStop(1, gradientColors[scaleId][1]);

                    d.backgroundColor = gradient;
                }
            }
        } catch(error) {
            console.log(error);
        }
    }

    const updateChart = () => {
        if (chart && chart.canvas) {
            // Update the dataset gradients
            chart.data.datasets.forEach((d) => {
                updateData(d);
            })

            chart.update();
        }
    }

    const handleResize = () => {
        if (resizeTimeoutId) {
            window.clearTimeout(resizeTimeoutId);
        }
        resizeTimeoutId = window.setTimeout(() => {
            setScreenWidth(window.innerWidth);
            updateChart();
            resizeTimeoutId = false;
        }, 50);
    }

    const setFontSize = () => {
        let size = 16;

        for (let breakpoint of widthBreakpoints) {
            if (screenWidth < breakpoint.width) {
                size = breakpoint.fontSize;
                break;
            }
        }

        Chart.defaults.font.size = size;
        if (chart && chart.canvas) {
            chart.update();
        }
    }

    return (
        <canvas ref={chartRef} />
    )
}